<template>
  <div class="product-container">
    <Video
      v-if="dataLoaded && videoData.topImg"
      :videoData="videoData"
      ref="videoComp"
    />

    <div class="feature-group">
      <Feature
        v-for="(item, i) in featureData"
        :key="i + 'featureGroup'"
        :data="item"
        :data-aos="'fade-up'"
        data-aos-duration="800"
        :data-aos-delay="i * 100"
      />
    </div>

    <div
      class="gallery"
      data-aos="fade-up"
      data-aos-duration="800"
      v-if="galleryImgs.length > 0"
    >
      <h2 class="title">Take a Closer Look</h2>
      <Gallery :images="galleryImgs" />
    </div>

    <ConfigurationOverview
      v-if="data.configurations"
      :data="data.configurations"
      data-aos="fade-up"
      data-aos-duration="800"
    />

    <div class="techSpecs" data-aos="fade-up" data-aos-duration="800">
      <TechSpecs
        :models="techSpecsData.models"
        :specGroup="techSpecsData.specGroup"
        :commonSpecs="techSpecsData.commonSpecs"
      />
    </div>
  </div>
</template>

<script>
import Video from "./components/Video";
import Feature from "./components/Feature";
import Gallery from "@/components/Gallery";
import ConfigurationOverview from "./components/ConfigurationOverview";
import TechSpecs from "./components/TechSpecs";
import { getProductDetail } from "@/api/productAPI.js";
import { setWatermark, removeWatermark } from "@/utils/waterMark.js";

import AOS from "aos";
import "aos/dist/aos.css";

export default {
  components: {
    Video,
    Feature,
    Gallery,
    ConfigurationOverview,
    TechSpecs,
  },
  data() {
    return {
      data: {},
      dataLoaded: false,
    };
  },
  computed: {
    videoData() {
      if (!this.data.topDisplay) return {};
      const isMobile = window.innerWidth <= 768;
      const top = this.data.topDisplay;
      return {
        title: top.title,
        subtitle: top.subtitle,
        tip: top.tip,
        video: isMobile ? top.videoMobile || "" : top.video || "",
        videoMobile: top.videoMobile || "",
        topImg: top.topImg || "",
        topImgMobile: top.topImgMobile || "",
      };
    },
    featureData() {
      return this.data.features || [];
    },
    galleryImgs() {
      if (!this.data.albums) return [];
      return this.data.albums.map((album) => ({
        large: "/assets/products/" + album.large,
        largeMobile: "/assets/products/" + album.largeMobile || album.large,
        small: "/assets/products/" + album.small || album.large,
        smallMobile:
          "/assets/products/" + album.smallMobile || album.small || album.large,
        title: album.title || "",
      }));
    },
    techSpecsData() {
      if (!this.data.models || !this.data.attributes)
        return { models: [], specGroup: [], commonSpecs: {} };
      const models = this.data.models.map((m) => ({ name: m, specs: {} }));
      const specGroup = this.data.attributes.map((g) => ({
        title: g.groupTitle,
        items: g.properties
          .map((p) => {
            const key = p.name.toLowerCase().replace(/\s+/g, "_");
            if (typeof p.value === "string")
              return { name: p.name, key, isCommon: true };
            else if (Array.isArray(p.value)) {
              p.value.forEach((v, idx) => {
                if (idx < models.length) models[idx].specs[key] = v;
              });
              return { name: p.name, key, isCommon: false };
            }
            return null;
          })
          .filter(Boolean),
      }));
      const commonSpecs = {};
      this.data.attributes.forEach((g) =>
        g.properties.forEach((p) => {
          if (typeof p.value === "string")
            commonSpecs[p.name.toLowerCase().replace(/\s+/g, "_")] = p.value;
        }),
      );
      return { models, specGroup, commonSpecs };
    },
  },
  created() {
    this.loadProduct(this.$route.params.id);
  },
  watch: {
    "$route.params.id"(newId, oldId) {
      if (newId && newId !== oldId) this.loadProduct(newId);
    },
  },
  mounted() {
    this.$emit("set-product-page", true);
    setWatermark("© YOLEE — yoleecorp.com");
  },
  beforeDestroy() {
    this.$emit("set-product-page", false);
    removeWatermark();
  },
  methods: {
    async loadProduct(id) {
      this.dataLoaded = false;
      try {
        const res = await getProductDetail(id);
        this.data = res;
        this.$nextTick(() => {
          const video = this.$refs.videoComp?.$refs.videoPlayer;
          if (video) {
            video.addEventListener("loadeddata", () => {
              if (window.AOS) window.AOS.refresh();
            });
          }
          this.dataLoaded = true;
        });
      } catch (err) {
        console.error("Load product failed:", err);
        this.dataLoaded = true;
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

* {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
}

/* 禁止长按选中文本（移动端） */
* {
  -webkit-touch-callout: none !important;
}

.product-container {
  padding: 1rem 2rem;
}

.gallery {
  margin: 1.5rem auto;

  .title {
    font: 700 28px/1.2 @font-en;
    color: @color-text-title;
    margin-bottom: 0.5rem;

    .mobile-only({
    font-size: 24px;
    });
  }
}
</style>
